import { CellPosition, copyPos, copyPosArray } from '@app/grid/GridCell'
import { enterEdit } from '@app/grid/editSession'
import { RowRangeAnchor } from '@app/grid/rowRangeSelection'
import { isCompozzError } from '@dto/CompozzError'
import { Dashboard } from '@dto/Dashboard'
import Field from '@dto/fields'
import { ObjectInfos, SharedObjectInfos } from '@dto/objects'
import { Period } from '@dto/period'
import { UserPreferences } from '@dto/preferences'
import { FieldColumn, Item, Line, MetaLine, PageInfo, PageRequest, PathsResponse } from '@dto/records'
import { ObjectRelation } from '@dto/relation'
import { Tenant } from '@dto/Tenant'
import User, { checkedSelection, newRole, Role } from '@dto/Users'
import { View } from '@dto/views'
import { Workspace } from '@dto/workspaces'
import { RowSelectionState } from '@tanstack/react-table'
import { applyBrightness, DEFAULT_COMPOZZ_COLOR, getHoverColor, getLightColor, IsDarkColor } from '@utils/colors'
import { FIRST_PAGE_SIZE, isProgressiveLoadingEnabled, nextPageSize, PREFERRED_LANG_KEY, USER_ORG_ID, USERNAME } from '@utils/constants'
import { clearData, fetchView } from '@utils/data'
import { fetchViewInfos, getDashboards } from '@utils/model'
import { applyGroupSummaries, shouldRefreshSummaries } from '@utils/summaries'
import { fetchTenant } from '@utils/tenant'
import { idToKey, toMetaLine, toMetaLineMap } from '@utils/types'
import { fetchRole, fetchUser, fetchUsers } from '@utils/users'
import { Locale } from 'date-fns'
import { TFunction } from 'i18next'
import { create } from 'zustand'


/**
 * App store
 */
type AppState = {
    locales: Map<string, Locale>,
    usersByKeys: Map<string, User>,
    tenant: Tenant | null,
}

type AppAction = {
    updateLocales: (locales: AppState['locales']) => void,
    updateUsers: (users: AppState['usersByKeys']) => void,
    fetchUsers: () => Promise<void>,
    fetchTenant: () => Promise<void>,
}

export const useAppStore = create<AppState & AppAction>((set) => ({
    locales: new Map<string, Locale>(),
    usersByKeys: new Map<string, User>(),
    tenant: null,
    updateLocales: (locales) => set({ locales }),
    updateUsers: (users) => set({ usersByKeys: users }),
    fetchUsers: async () => {
        const users = await fetchUsers();
        const usersByKeys = new Map<string, User>();
        if (users && users.length > 0) {
            users.forEach(user => {
                usersByKeys.set(user._key, user);
            });
        }
        set(() => ({
            usersByKeys: usersByKeys
        }));
    },
    fetchTenant: async () => {
        const tenant = await fetchTenant();
        set(() => ({
            tenant: tenant
        }));
        // Update model store colors when tenant changes
        useModelStore.getState().updateTenantColors();
    },
}))

/**
 * User store
 */
type UserState = {
    username: string | undefined,
    user: User | undefined,
    lang: string,
    orgId: string | undefined,
}

type UserAction = {
    updateUsername: (username: string | undefined) => void,
    updateLang: (lang: string) => void,
    updateOrgId: (orgId: string | undefined) => void,
}

export const useUserStore = create<UserState & UserAction>((set) => ({
    username: undefined,
    user: undefined,
    lang: "en-EN",
    orgId: undefined,
    updateUsername: async (username) => {
        if (username) {
            localStorage.setItem(USERNAME, username);
        } else {
            localStorage.removeItem(USERNAME);
        }
        set({ username });
        if (username) {
            fetchUser(username).then((user) => {
                set({ user });
            });
        }
    },
    updateLang: (lang) => {
        localStorage.setItem(PREFERRED_LANG_KEY, lang);
        set({ lang });
    },
    updateOrgId: (orgId) => {
        if (orgId) {
            localStorage.setItem(USER_ORG_ID, orgId);
        } else {
            localStorage.removeItem(USER_ORG_ID);
        }
        set({ orgId });
    },
}))


/**
 * Model store
 */
type ModelState = {
    // Model workspaces
    workspaces: Workspace[],
    // Workspace by key map
    workspaceByKeys: Map<string, Workspace>,
    // Selected workspace
    selectedWorkspace: Workspace | null,
    // Selected dashboard
    selectedDashboard: Dashboard | null,
    // Workspace color
    wsColor: string,
    // Dark workspace color
    wsDarkColor: string,
    // Light workspace color
    wsLightColor: string,
    // Is workspace color dark?
    wsIsDarkColor: boolean,
    // Hover workspace color
    wsHoverColor: string,
    // Model objects (of the selected workspace)
    objectInfos: ObjectInfos[],
    // Shared objects
    sharedObjectInfos: SharedObjectInfos[],
    // Selected object
    selectedObjectInfos: ObjectInfos | null,
    // Selected view
    selectedView: View | null,
    // Object by key map
    objectByKeys: Map<string, ObjectInfos> | null,
    // View by key map
    viewByKeys: Map<string, View> | null,
    // Field by key map
    fieldByKeys: Map<string, Field> | null,
    // Relation by key map
    relationByKeys: Map<string, ObjectRelation> | null,
    // Outbound relations
    outboundRelations: Map<string, ObjectRelation[]> | null,
    // Inbound relations
    inboundRelations: Map<string, ObjectRelation[]> | null,
    // Current user role on the selected workspace
    userRole: Role | null,
    // Dashboards
    dashboards: Dashboard[] | null,
    // Personal preferences (view folders) of the current user, by object key
    viewPreferencesByObjectKey: Map<string, UserPreferences>,
}

type ModelAction = {
    updateWorkspaces: (workspaces: ModelState['workspaces']) => void
    selectWorkspace: (workspace: ModelState['selectedWorkspace']) => void
    updateObjectInfos: (objectInfos: ModelState['objectInfos'], selectedObjectInfos: ModelState['selectedObjectInfos'], selectedView: ModelState['selectedView'], selectedDashboard: ModelState['selectedDashboard'], sharedObjectInfos: ModelState['sharedObjectInfos']) => void
    selectObjectInfos: (selectedObjectInfos: ModelState['selectedObjectInfos'], selectedView: ModelState['selectedView'], selectedDashboard: ModelState['selectedDashboard']) => void
    updateSelectedObjectInfos: (selectedObjectInfos: ModelState['selectedObjectInfos'], selectedView: ModelState['selectedView'], selectedDashboard: ModelState['selectedDashboard'], sharedObjectInfos: ModelState['sharedObjectInfos']) => void
    selectView: (objectInfos: ModelState['selectedObjectInfos'], selectedView: ModelState['selectedView'], selectedDashboard: ModelState['selectedDashboard']) => void
    updateSelectedView: (objectInfos: ModelState['selectedObjectInfos'], selectedView: ModelState['selectedView'], selectedDashboard: ModelState['selectedDashboard'], sharedObjectInfos: ModelState['sharedObjectInfos']) => void
    updateUserRole: (userRole: ModelState['userRole']) => void
    fetchUserRole: (workspaceKey: string | null | undefined) => void
    fetchDashboards: (workspaceKey: string | null | undefined, t: TFunction) => void
    selectDashboard: (dashboard: ModelState['selectedDashboard']) => void
    updateSelectedDashboard: (dashboard: ModelState['selectedDashboard']) => void
    updateDashboards: (dashboards: ModelState['dashboards']) => void
    updateSingleObjectInfo: (objectInfos: ModelState['objectInfos'],singleObjectInfos: ObjectInfos) => void
    fetchViewInfos: (workspaceKey: string, objectKey: string, viewKey: string, onViewUpdated: (viewInfos: View) => void) => void
    updateTenantColors: () => void
    setWorkspacePreferences: (preferences: UserPreferences[]) => void
    setObjectPreferences: (objectKey: string, preferences: UserPreferences) => void
}

export const useModelStore = create<ModelState & ModelAction>((set) => ({
    workspaces: [],
    workspaceByKeys: new Map<string, Workspace>(),
    selectedWorkspace: null,
    wsColor: useAppStore.getState().tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR,
    wsDarkColor: applyBrightness(useAppStore.getState().tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR, -25),
    wsLightColor: getLightColor(useAppStore.getState().tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR),
    wsHoverColor: getHoverColor(useAppStore.getState().tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR),
    wsIsDarkColor: IsDarkColor(useAppStore.getState().tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR),
    objectInfos: [],
    sharedObjectInfos: [],
    selectedObjectInfos: null,
    selectedView: null,
    objectByKeys: null,
    viewByKeys: null,
    fieldByKeys: null,
    relationByKeys: null,
    outboundRelations: null,
    inboundRelations: null,
    userRole: null,
    dashboards: null,
    selectedDashboard: null,
    viewPreferencesByObjectKey: new Map<string, UserPreferences>(),
    updateWorkspaces: (workspaces) => set(() => {
        const workspaceByKeys = new Map<string, Workspace>();
        workspaces.forEach(workspace => {
            workspaceByKeys.set(workspace._key, workspace);
        });
        return ({ workspaces: workspaces, workspaceByKeys: workspaceByKeys });
    }),
    selectWorkspace: (workspace) => set(() => {
        clearData(true);

        const wsColor = workspace?.color ?? useAppStore.getState().tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR;
        const isDarkColor = IsDarkColor(wsColor);
        const darkColor = applyBrightness(wsColor, -25);
        const lightColor = getLightColor(wsColor);
        const hoverColor = getHoverColor(wsColor);
        if (workspace?._key) {
            const fetchUserRole = useModelStore.getState().fetchUserRole;
            fetchUserRole(workspace?._key);
        }
        return ({ selectedWorkspace: workspace, selectedObjectInfos: null, selectedView: null, wsColor: wsColor, wsDarkColor: darkColor, wsLightColor: lightColor, wsHoverColor: hoverColor, wsIsDarkColor: isDarkColor, viewPreferencesByObjectKey: new Map<string, UserPreferences>() })
    }),
    setWorkspacePreferences: (preferences) => set(() => {
        const viewPreferencesByObjectKey = new Map<string, UserPreferences>();
        preferences.forEach(prefs => {
            if (prefs.objectKey) {
                viewPreferencesByObjectKey.set(prefs.objectKey, prefs);
            }
        });
        return ({ viewPreferencesByObjectKey });
    }),
    setObjectPreferences: (objectKey, preferences) => set((state) => {
        const viewPreferencesByObjectKey = new Map(state.viewPreferencesByObjectKey);
        viewPreferencesByObjectKey.set(objectKey, preferences);
        return ({ viewPreferencesByObjectKey });
    }),
    selectObjectInfos: (selectedObjectInfos, selectedView) => set(() => {
        clearData();
        return checkedSelection(selectedObjectInfos, selectedView, null)
    }),
    selectView: (objectInfos, selectedView) => set(() => {
        clearData();
        return checkedSelection(objectInfos, selectedView, null);
    }),
    selectDashboard: (selectedDashboard) => set(() => {
        clearData();
        return checkedSelection(null, null, selectedDashboard);
    }),
    updateSelectedObjectInfos: (selectedObjectInfos, selectedView, selectedDashboard, sharedObjectInfos) => {
        const objectInfos = useModelStore.getState().objectInfos;
        const updateObjectInfos = useModelStore.getState().updateObjectInfos;
        let newObjectInfos = objectInfos;
        if (selectedObjectInfos) {
            newObjectInfos = objectInfos.map(obj => obj.object._key === selectedObjectInfos.object._key ? selectedObjectInfos : obj);
        }
        updateObjectInfos(newObjectInfos, selectedObjectInfos, selectedView, selectedDashboard, sharedObjectInfos);
    },
    updateObjectInfos: (objectInfos, selectedObjectInfos, selectedView, selectedDashboard, sharedObjectInfos) => set(() => {
        const fieldByKeys = new Map<string, Field>();
        const objectByKeys = new Map<string, ObjectInfos>();
        const viewByKeys = new Map<string, View>();
        const relationByKeys = new Map<string, ObjectRelation>();
        const outboundRelations = new Map<string, ObjectRelation[]>();
        const inboundRelations = new Map<string, ObjectRelation[]>();

        if (objectInfos && objectInfos.length > 0) {
            objectInfos.forEach((objectInfo) => {
                objectByKeys.set(objectInfo.object._key, objectInfo);
                if (objectInfo.fields && objectInfo.fields.length > 0) {
                    objectInfo.fields.forEach((field) => {
                        fieldByKeys.set(field._key, field);
                    });
                }
                if (objectInfo.views && objectInfo.views.length > 0) {
                    objectInfo.views.forEach((view) => {
                        viewByKeys.set(view._key, view);
                    });
                }
                if (objectInfo.relations && objectInfo.relations.length > 0) {
                    objectInfo.relations.forEach((relation) => {
                        relationByKeys.set(relation._key, relation);
                    });
                }
                outboundRelations.set(objectInfo.object._key, (objectInfo.relations ?? []).filter(r => idToKey(r._from) === objectInfo.object._key));
                inboundRelations.set(objectInfo.object._key, (objectInfo.relations ?? []).filter(r => idToKey(r._to) === objectInfo.object._key));
            });
        }

        if (sharedObjectInfos && sharedObjectInfos.length > 0) {
            sharedObjectInfos.forEach((sharedObjectInfo) => {
                if (sharedObjectInfo.objectInfos && sharedObjectInfo.objectInfos.length > 0) {
                    sharedObjectInfo.objectInfos.forEach((objectInfo) => {
                        objectByKeys.set(objectInfo.object._key, objectInfo);
                        if (objectInfo.fields && objectInfo.fields.length > 0) {
                            objectInfo.fields.forEach((field) => {
                                fieldByKeys.set(field._key, field);
                            });
                        }
                        if (objectInfo.views && objectInfo.views.length > 0) {
                            objectInfo.views.forEach((view) => {
                                viewByKeys.set(view._key, view);
                            });
                        }
                        if (objectInfo.relations && objectInfo.relations.length > 0) {
                            objectInfo.relations.forEach((relation) => {
                                relationByKeys.set(relation._key, relation);
                            });
                        }
                        outboundRelations.set(objectInfo.object._key, (objectInfo.relations ?? []).filter(r => idToKey(r._from) === objectInfo.object._key));
                        inboundRelations.set(objectInfo.object._key, (objectInfo.relations ?? []).filter(r => idToKey(r._to) === objectInfo.object._key));
                    });
                }
            });
        }

        const { selectedObjectInfos: objectInfoToSelect, selectedView: viewToSelect, selectedDashboard: dashboardToSelect } = checkedSelection(selectedObjectInfos, selectedView, selectedDashboard);
        return { objectInfos: objectInfos, selectedObjectInfos: objectInfoToSelect, selectedView: viewToSelect, selectedDashboard: dashboardToSelect, fieldByKeys: fieldByKeys, objectByKeys: objectByKeys, relationByKeys: relationByKeys, outboundRelations: outboundRelations, inboundRelations: inboundRelations, sharedObjectInfos: sharedObjectInfos }
    }),
    updateSelectedView: (objectInfos, selectedView, selectedDashboard, sharedObjectInfos) => {
        const updateObjectInfos = useModelStore.getState().updateObjectInfos;
        let allObjectInfos = useModelStore.getState().objectInfos;

        if (objectInfos && selectedView) {
            let newObjectInfos = { ...objectInfos, views: objectInfos.views.map(view => view._key === selectedView._key ? selectedView : view) };
            allObjectInfos = allObjectInfos.map(obj => obj.object._key === objectInfos.object._key ? newObjectInfos : obj);
        }
        updateObjectInfos(allObjectInfos, objectInfos, selectedView, selectedDashboard, sharedObjectInfos);
    },
    updateUserRole: (userRole) => {
        const user = useUserStore.getState().user;
        let newUserRole = userRole;
        if (newUserRole) {
            newUserRole.isOwner = user?.owner ?? false;
        } else {
            newUserRole = newRole(user?.owner ?? false);
        }
        set({ userRole: newUserRole });
    },
    fetchUserRole: (workspaceKey: string | null | undefined) => {
        const user = useUserStore.getState().user;
        if (workspaceKey && user) {
            fetchRole(workspaceKey).then((userRole) => {
                let newUserRole = userRole;
                if (newUserRole) {
                    newUserRole.isOwner = user?.owner ?? false;
                } else {
                    newUserRole = newRole(user?.owner ?? false);
                }
                set({ userRole: newUserRole });
            });
        } else {
            let newUserRole = newRole(user?.owner ?? false);
            set({ userRole: newUserRole });
        }
    },
    fetchDashboards: (workspaceKey: string | null | undefined, t: TFunction) => {
        if (workspaceKey) {
            getDashboards(workspaceKey, (dashboards) => set({ dashboards: dashboards }), t);
        }
    },
    updateDashboards: (dashboards) => set(() => ({ dashboards: dashboards })),
    updateSelectedDashboard: (dashboard) => {
        const { selectedDashboard: dashboardToSelect } = checkedSelection(null, null, dashboard);
        set(() => ({ selectedDashboard: dashboardToSelect }))
    },
    updateSingleObjectInfo(objectInfos, singleObjectInfos) {
        const newObjectInfos = objectInfos.map( o => (o.object._key == singleObjectInfos.object._key) ? singleObjectInfos : o)
        set(() => ({ objectInfos: newObjectInfos }))
    },
    fetchViewInfos: (workspaceKey: string, objectKey: string, viewKey: string, onViewUpdated: (viewInfos: View) => void) => {
        fetchViewInfos(workspaceKey, objectKey, viewKey, (viewInfos) => set(() => {
            let newSelectedView : View | null = useModelStore.getState().selectedView ?? null;
            if (newSelectedView && newSelectedView._key === viewKey) {
                newSelectedView = viewInfos;
            }

            let newSelectedObjectInfos : ObjectInfos | null = useModelStore.getState().selectedObjectInfos ?? null;
            if (newSelectedObjectInfos && newSelectedObjectInfos.object._key === objectKey) {
                newSelectedObjectInfos = {
                    ...newSelectedObjectInfos,
                    views: newSelectedObjectInfos.views.map(v => v._key === viewKey ? viewInfos : v)
                };
            }

            let newObjectInfos : ObjectInfos[] = useModelStore.getState().objectInfos ?? [];
            if (newObjectInfos && newObjectInfos.length > 0) {
                newObjectInfos = newObjectInfos.map(o => o.object._key === objectKey ? {
                    ...o,
                    views: o.views.map(v => v._key === viewKey ? viewInfos : v)
                } : o);
            }

            let newSharedObjectInfos : SharedObjectInfos[] = useModelStore.getState().sharedObjectInfos ?? [];
            if (newSharedObjectInfos && newSharedObjectInfos.length > 0) {
                newSharedObjectInfos = newSharedObjectInfos.map(so => so.wsKey === workspaceKey ? {
                    ...so,
                    objectInfos: so.objectInfos.map(oi => oi.object._key === objectKey ? {
                        ...oi,
                        views: oi.views.map(v => v._key === viewKey ? viewInfos : v)
                    } : oi)
                } : so);
            }

            let newObjectByKeys : Map<string, ObjectInfos> = useModelStore.getState().objectByKeys ?? new Map<string, ObjectInfos>();
            if (newObjectByKeys && newObjectByKeys.size > 0) {
                const newObject = newObjectByKeys.get(objectKey);
                if (newObject) {
                    newObjectByKeys.set(objectKey, {
                        ...newObject,
                        views: newObject.views.map(v => v._key === viewKey ? viewInfos : v)
                    });
                }
            }

            let newViewByKeys : Map<string, View> = useModelStore.getState().viewByKeys ?? new Map<string, View>();
            if (newViewByKeys && newViewByKeys.size > 0) {
                newViewByKeys.set(viewKey, viewInfos);
            }

            setTimeout(() => {
                onViewUpdated(viewInfos);
            }, 1000);
            
            return { 
                selectedView: newSelectedView, 
                selectedObjectInfos: newSelectedObjectInfos, 
                objectInfos: newObjectInfos, 
                sharedObjectInfos: newSharedObjectInfos, 
                objectByKeys: newObjectByKeys, 
                viewByKeys: newViewByKeys
            }
        }));
    },
    updateTenantColors: () => set((state) => {
        const tenant = useAppStore.getState().tenant;
        const selectedWorkspace = state.selectedWorkspace;
        // Only update colors if no workspace is selected or workspace has no color
        if (!selectedWorkspace || !selectedWorkspace.color) {
            const customColor = tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR;
            return {
                wsColor: customColor,
                wsDarkColor: tenant?.customDarkColor ?? applyBrightness(customColor, -25),
                wsLightColor: tenant?.customLightColor ?? getLightColor(customColor),
                wsHoverColor: getHoverColor(customColor),
                wsIsDarkColor: IsDarkColor(customColor),
            };
        }
        return {};
    }),
}))

type DataState = {
    fieldColumns: FieldColumn[],
    data: Line[],
    pivot: Field | null,
    period: Period | null,
    metaLines: Map<string, MetaLine>,
    lineNumber: number,
    colNumber: number,
    groupNumber: number,
    focusPos: CellPosition[] | null,
    editingPos: CellPosition | null,
    // Character the user typed to start editing (null when entered via
    // click/Enter); link cells read it to seed their relation search.
    editInitialKey: string | null,
    selectionAnchor: CellPosition | null,
    rowSelection: RowSelectionState,
    // Anchor of the checkbox range selection (Shift+click on the select
    // column), distinct from selectionAnchor which anchors the CELL rectangular
    // selection. Its validity is checked at click time (nextRowSelectionOnClick
    // compares the selection object it last wrote with the current one), so no
    // code path has to reset it.
    rowRangeAnchor: RowRangeAnchor | null,
    loadingData: boolean,
    rowActionsEnabled: boolean,
    // Progressive loading indicator: non-null while background pages of the
    // current view are still being fetched (invisible pagination).
    loadProgress: { loaded: number, total: number } | null,
    // Values of the view summaries over the whole result set, index-aligned
    // with fieldColumns. Arrives with the response for non-paginated views,
    // or with the LAST page under invisible pagination; null until then.
    summaries: (any | null)[] | null,
    // Row ceiling actually sent with the last fetch (the "Afficher toutes les
    // lignes" toggle), or null when the whole result set was requested. Kept in
    // the store rather than read from localStorage at render time so toggling
    // the switch immediately reaches the grid (truncation notice, add-record
    // button) instead of waiting for a remount.
    appliedLimit: number | null,
}

type DataAction = {
    updateData: (data: DataState['data'], fieldColumns: DataState['fieldColumns']) => void,
    updateFieldColumns: (fieldColumns: DataState['fieldColumns']) => void,
    updateDataOnly: (data: DataState['data']) => void,
    // applyCellValues optimistically sets cell values in place (immutably) by
    // (objectKey, recordKey, fieldKey), without a refetch — used by undo/redo of
    // updates so the grid changes instantly instead of waiting for fetchData.
    applyCellValues: (cells: { objectKey: string; recordKey: string; fieldKey: string; value: any }[]) => void,
    // applyItemValues optimistically sets the value of specific cell items, matched
    // by item REFERENCE (not key), without a refetch. Link cells share a
    // destination-based key across rows, so a key match would wrongly clear other
    // rows pointing at the same target; identity is unique per cell. Recurses into
    // grouped children so a deleted link's badge disappears instantly.
    applyItemValues: (items: Item[], value: any) => void,
    fetchData: () => Promise<void>,
    fetchView: (view: View | null) => Promise<void>,
    fetchLine: (lineId: string, recordKeys: string[] | null, view: View | null) => Promise<void>,
    insertNewRecord: (lineId: string) => void,
    focus: (position: CellPosition | null) => void,
    focusMultiple: (positions: CellPosition[] | null) => void,
    edit: (position: CellPosition | null, initialKey?: string | null) => void,
    // selectRows: (rows: string[]) => void,
    setRowSelection: (rowSelection: RowSelectionState) => void,
    setRowRangeAnchor: (anchor: RowRangeAnchor | null) => void,
    setSelectionAnchor: (anchor: CellPosition | null) => void,
    // requestSave: () => void,
    loadData: () => void,
    stopLoadingData: () => void,
    updatePeriod: (period: Period | null) => void,
    enableRowActions: (enable: boolean) => void,
    updateMetaLines: (metaLines: Map<string, MetaLine>) => void,
    insertRecordIntoGroup: (parentRowId: string | null, newLine: Line) => string,
    removeLines: (lineIds: string[]) => void,
    // refreshSummaries re-asks the backend for the view summaries (debounced,
    // summariesOnly request): called after light mutations (cell edits, link
    // changes) that do not go through a full fetchData.
    refreshSummaries: () => void,
}

// scheduleSummariesRefresh re-asks the BACKEND for the view summaries after a
// light mutation (cell edit, link change) — the flows that do not already go
// through a full fetchData. The request carries summariesOnly: the server
// runs the normal pipeline (formulas included) over the whole result set but
// only returns the totals and the group skeleton, so the payload stays tiny
// and the grid is not re-rendered. Debounced to coalesce bursts of edits;
// every value is computed backend-side, the front never aggregates anything.
let summariesRefreshTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSummariesRefresh() {
    const view = useModelStore.getState().selectedView;
    if (!shouldRefreshSummaries(view, useDataStore.getState().summaries)) {
        return;
    }
    if (summariesRefreshTimer) {
        clearTimeout(summariesRefreshTimer);
    }
    summariesRefreshTimer = setTimeout(async () => {
        summariesRefreshTimer = null;
        const currentView = useModelStore.getState().selectedView;
        if (!shouldRefreshSummaries(currentView, useDataStore.getState().summaries)) {
            return;
        }
        const period = useDataStore.getState().period;
        const response = await fetchView(currentView, period, null, undefined, null, true);
        if (!response || isCompozzError(response)) {
            return;
        }
        const pathsResponse = response as PathsResponse;
        if (!pathsResponse.summaries) {
            return;
        }
        useDataStore.setState((state) => {
            // The summaries are index-aligned with the response columns: if
            // the column shape drifted from the loaded grid (e.g. a pivot
            // bucket appeared), skip — the next full fetch reconciles.
            if (pathsResponse.summaries!.length !== state.fieldColumns.length) {
                return {};
            }
            return {
                summaries: pathsResponse.summaries,
                data: applyGroupSummaries(state.data, pathsResponse.lines ?? [], state.groupNumber),
            };
        });
    }, 400);
}

function removeLinesFromData(lines: Line[], ids: Set<string>, parentIsLeftGrouped: boolean = false): Line[] {
    return lines
        .filter(line => !ids.has(line._rowId ?? "") || parentIsLeftGrouped)
        .map(line => {
            if (ids.has(line._rowId ?? "") && parentIsLeftGrouped) {
                // Child of a left-grouped parent: preserve the row but clear its own fields and make all readonly
                return {
                    ...line,
                    items: line.items.map(item => ({
                        ...item,
                        value: item.parentKey === null ? null : item.value,
                        readOnly: true,
                    })),
                    children: line.children ? removeLinesFromData(line.children, ids, line.isLeftGrouped) : null,
                };
            }
            return {
                ...line,
                children: line.children ? removeLinesFromData(line.children, ids, line.isLeftGrouped) : null,
            };
        });
}

function insertOrReplaceLineAtPath(data: Line[], pathParts: number[], newLine: Line): Line[] {
    if (pathParts.length === 0) {
        return [...data, newLine];
    }
    const [head, ...tail] = pathParts;
    const newData = [...data];
    const parent = newData[head];
    if (!parent) return newData;
    const updatedParent = { ...parent };
    if (tail.length === 0) {
        const children = parent.children ?? [];
        const lastIndex = parseInt(newLine._rowId?.split(".").pop() ?? "", 10);
        if (!isNaN(lastIndex) && lastIndex < children.length) {
            updatedParent.children = children.map((child, i) => i === lastIndex ? newLine : child);
        } else {
            updatedParent.children = [...children, newLine];
        }
    } else {
        updatedParent.children = insertOrReplaceLineAtPath(parent.children ?? [], tail, newLine);
    }
    newData[head] = updatedParent;
    return newData;
}

// Single controller shared by the grid fetches (fetchView/fetchData actions): they
// all write into the same store displayed by the single grid, so only the latest
// in-flight request may apply its result. Starting a new fetch aborts the previous
// one, whatever view or object it was for.
let fetchViewAbortController: AbortController | null = null;

function abortAndRenewController(): AbortController {
    fetchViewAbortController?.abort();
    fetchViewAbortController = new AbortController();
    return fetchViewAbortController;
}

// Cancel the in-flight grid fetch without starting a new one (e.g. when leaving
// the grid for configuration or dashboards: the late result would be written to
// a store nobody displays anymore).
export function abortViewFetch() {
    fetchViewAbortController?.abort();
    fetchViewAbortController = null;
}

// Immutably rebuild the line tree, replacing the value of every item for which
// `resolve(item)` returns a value box ([newValue]); returning null leaves the
// item untouched. Recurses into grouped children and only rebuilds changed lines
// (new refs) so react-table re-renders just the affected rows. Shared by the two
// optimistic-update actions, which differ only in how they match items (by key
// vs by reference) and what value they apply.
// --- Progressive loading (invisible pagination) -------------------------------
//
// Flat views (no groups, no pivot) are fetched page by page: the first small
// page renders immediately, the remaining pages load sequentially in the
// background and are appended to the store. The server is the authority on
// eligibility: when a response carries no `page` echo, the full result set was
// returned and the loop stops. All requests share the view AbortController, so
// switching views cancels the background loop. If the total drifts while
// paging (concurrent mutations), the whole load is silently re-run once.

const isPageableView = (view: View | null): boolean =>
    isProgressiveLoadingEnabled() &&
    !(view?.groups?.length) &&
    !view?.pivot?.pivotPathKey;

// `progressive: false` fetches the full result in one request: the current rows
// stay on screen until the fresh ones replace them atomically. Used for
// post-mutation refreshes (fetchData), where restarting the page loop would
// visibly collapse the grid to the first page before re-growing it.
async function loadViewProgressively(view: View | null, retried: boolean = false, progressive: boolean = true): Promise<void> {
    const period = useDataStore.getState().period;
    const controller = abortAndRenewController();
    const firstPage: PageRequest | null = progressive && isPageableView(view) ? { offset: 0, size: FIRST_PAGE_SIZE } : null;
    const response = await fetchView(view, period, null, controller.signal, firstPage);
    if (controller.signal.aborted) return;
    let pathsResponse: PathsResponse = { lines: [], columns: [], pivot: null };
    if (response) {
        pathsResponse = response as PathsResponse;
    }
    const lines = pathsResponse.lines ?? [];
    const metaLines = toMetaLineMap(lines);  // Also assigns _rowId to each line
    const page = pathsResponse.page ?? null;
    useDataStore.setState((state) => ({
        data: lines,
        fieldColumns: pathsResponse.columns ?? [],
        pivot: pathsResponse.pivot,
        groupNumber: view?.groups?.length ?? 0,
        lineNumber: lines.length,
        colNumber: pathsResponse.columns?.length ?? 0,
        metaLines: metaLines,
        loadProgress: page?.hasMore ? { loaded: lines.length, total: page.total } : null,
        // Under invisible pagination the summaries only arrive with the last
        // page; until then keep the totals row hidden.
        summaries: pathsResponse.summaries ?? null,
        // Preserve focus and editing positions
        focusPos: state.focusPos,
        editingPos: state.editingPos
    }));
    if (page?.hasMore) {
        // Start the background loop only AFTER the first page has painted:
        // launched immediately, its response often lands before the grid
        // finishes mounting, so the very first paint would already contain
        // everything and the user would see no progressive gain. rAF fires
        // right before the paint; the nested timeout lands right after it.
        const remainingPage = page;
        window.requestAnimationFrame(() => {
            window.setTimeout(() => {
                void loadRemainingPages(view, period, controller, remainingPage, retried);
            }, 0);
        });
    }
}

async function loadRemainingPages(view: View | null, period: Period | null, controller: AbortController, firstPage: PageInfo, retried: boolean): Promise<void> {
    let offset = firstPage.offset + firstPage.size;
    let pageIndex = 0;
    let totalDrifted = false;
    while (true) {
        if (controller.signal.aborted) return;
        const size = nextPageSize(pageIndex);
        const response = await fetchView(view, period, null, controller.signal, { offset: offset, size: size });
        if (controller.signal.aborted) return;
        const pageResponse = response ? (response as PathsResponse) : null;
        const page = pageResponse?.page ?? null;
        if (!pageResponse || !page) {
            // Error or the server stopped paginating: keep what we have.
            useDataStore.setState({ loadProgress: null });
            return;
        }
        if (page.total !== firstPage.total) {
            totalDrifted = true;
        }
        useDataStore.setState((state) => {
            const data = state.data.concat(pageResponse.lines ?? []);
            return {
                data: data,
                lineNumber: data.length,
                metaLines: toMetaLineMap(data),  // append-only => existing _rowIds stay stable
                loadProgress: page.hasMore ? { loaded: data.length, total: page.total } : null,
                // The last page carries the summaries over the whole result set
                summaries: pageResponse.summaries ?? state.summaries,
                focusPos: state.focusPos,
                editingPos: state.editingPos
            };
        });
        if (!page.hasMore) {
            break;
        }
        offset += size;
        pageIndex++;
    }
    // The dataset changed while paging: rows may be duplicated or missing.
    // One silent full reload reconciles; a second drift is left as-is.
    if (totalDrifted && !retried && !controller.signal.aborted) {
        void loadViewProgressively(view, true);
    }
}

function rebuildItemValues(lines: Line[], resolve: (item: Item) => [any] | null): Line[] {
    const updateLine = (line: Line): Line => {
        let changed = false;
        const items = line.items.map(it => {
            const newItems = resolve(it);
            if (newItems) {
                changed = true;
                return { ...it, value: newItems[0] };
            }
            return it;
        });
        let children = line.children;
        if (line.children && line.children.length > 0) {
            const newChildren = line.children.map(updateLine);
            if (newChildren.some((c, i) => c !== line.children![i])) {
                children = newChildren;
                changed = true;
            }
        }
        return changed ? { ...line, items, children } : line;
    };
    return lines.map(updateLine);
}

export const useDataStore = create<DataState & DataAction>((set) => ({
    fieldColumns: [],
    data: [],
    pivot: null,
    period: null,
    metaLines: new Map<string, MetaLine>(),
    lineNumber: 0,
    colNumber: 0,
    groupNumber: 0,
    focusPos: null,
    editingPos: null,
    editInitialKey: null,
    selectionAnchor: null,
    rowSelection: {},
    rowRangeAnchor: null,
    loadingData: false,
    rowActionsEnabled: true,
    loadProgress: null,
    summaries: null,
    appliedLimit: null,
    updateData: (data, fieldColumns) => set((state) => (
        {
            data: data,
            lineNumber: data.length,
            fieldColumns: fieldColumns,
            colNumber: fieldColumns.length,
            metaLines: toMetaLineMap(data),  // Also assigns _rowId to each line
            summaries: null,
            // Preserve focus and editing positions
            focusPos: state.focusPos,
            editingPos: state.editingPos
        }
    )),
    updateFieldColumns: (fieldColumns) => set((state) => {
        clearData();
        return ({ 
            fieldColumns: fieldColumns,
            // Preserve focus and editing positions
            focusPos: state.focusPos,
            editingPos: state.editingPos
        })
    }),
    updateDataOnly: (data) => set((state) => (
        {
            data: data,
            lineNumber: data.length,
            metaLines: toMetaLineMap(data),  // Also assigns _rowId to each line
            // Preserve focus and editing positions
            focusPos: state.focusPos,
            editingPos: state.editingPos
        }
    )),
    applyCellValues: (cells) => set((state) => {
        if (cells.length === 0) {
            return {};
        }
        const keyOf = (k: { objectKey: string; recordKey: string; fieldKey: string }) =>
            `${k.objectKey}|${k.recordKey}|${k.fieldKey}`;
        const byKey = new Map(cells.map(c => [keyOf(c), c.value]));
        // Match by (objectKey, recordKey, fieldKey): the undo log carries keys, not
        // references to the live items.
        const newData = rebuildItemValues(state.data, it =>
            it.key?.recordKey && byKey.has(keyOf(it.key)) ? [byKey.get(keyOf(it.key))] : null);
        return {
            data: newData,
            metaLines: toMetaLineMap(newData),
            focusPos: state.focusPos,
            editingPos: state.editingPos,
        };
    }),
    applyItemValues: (items, value) => set((state) => {
        if (items.length === 0) {
            return {};
        }
        const targets = new Set(items);
        // Match by item reference: a link cell's key points to the shared
        // destination record, so a key match would clear unrelated rows.
        const newData = rebuildItemValues(state.data, it => targets.has(it) ? [value] : null);
        return {
            data: newData,
            metaLines: toMetaLineMap(newData),
            focusPos: state.focusPos,
            editingPos: state.editingPos,
        };
    }),
    fetchView: async (view: View | null) => {
        await loadViewProgressively(view);
    },
    fetchLine: async (lineId: string, recordKeys: string[] | null, view: View | null) => {
        const period = useDataStore.getState().period;
        const response = await fetchView(view, period, recordKeys);
        var pathsResponse: PathsResponse = { lines: [], columns: [], pivot: null };
        const state = useDataStore.getState();
        let newMetaLines = new Map<string, MetaLine>(state.metaLines);
        let newData: Line[] | null = null;
        if (response) {
            pathsResponse = response as PathsResponse;
            if (pathsResponse.lines && pathsResponse.lines.length > 0) {
                const firstLine = pathsResponse.lines[0];
                if (firstLine) {
                    const firstLineMetaLine = toMetaLine(firstLine, 0, null, 0);
                    if (firstLineMetaLine) {
                        firstLineMetaLine.id = lineId;
                        newMetaLines.set(lineId, firstLineMetaLine);
                    }
                    const newLineWithRowId = { ...firstLine, _rowId: lineId };
                    const parts = lineId.split(".");
                    if (parts.length === 1) {
                        const idx = parseInt(parts[0], 10);
                        if (!isNaN(idx) && idx < state.data.length) {
                            newData = state.data.map((line, i) => i === idx ? newLineWithRowId : line);
                        }
                    } else {
                        const pathParts = parts.slice(0, -1).map(Number);
                        newData = insertOrReplaceLineAtPath(state.data, pathParts, newLineWithRowId);
                    }
                }
            }
        }
        set(() => ({
            metaLines: newMetaLines,
            ...(newData ? { data: newData } : {}),
        }));
    },
    insertNewRecord: (lineId: string) => {
        const state = useDataStore.getState();
        const newMetaLine = state.metaLines.get(lineId);
        if (!newMetaLine) return;

        const newIndex = state.data.length;
        const newLine = newMetaLine.line;
        newLine._rowId = String(newIndex);

        const realMetaLine: MetaLine = {
            id: String(newIndex),
            line: newLine,
            parentId: null,
            childrenIds: [],
            level: 0,
        };

        const newMetaLines = new Map<string, MetaLine>(state.metaLines);
        newMetaLines.delete(lineId);
        newMetaLines.set(String(newIndex), realMetaLine);

        set((s) => ({
            data: [...s.data, newLine],
            lineNumber: s.data.length + 1,
            metaLines: newMetaLines,
            focusPos: s.focusPos,
            editingPos: s.editingPos,
        }));
    },
    fetchData: async () => {
        await loadViewProgressively(useModelStore.getState().selectedView, false, false);
    },
    removeLines: (lineIds: string[]) => {
        const ids = new Set(lineIds);
        set((state) => {
            const newData = removeLinesFromData(state.data, ids);
            const newMetaLines = new Map(state.metaLines);
            lineIds.forEach(id => newMetaLines.delete(id));
            return { data: newData, metaLines: newMetaLines, lineNumber: newData.length };
        });
    },
    focusMultiple: (positions) => set(() => ({ focusPos: copyPosArray(positions), editingPos: null, editInitialKey: null })),
    focus: (position) => set(() => ({ focusPos: (position) ? [copyPos(position)!] : null, editingPos: null, editInitialKey: null })),
    edit: (position, initialKey = null) => set(() => enterEdit(position, initialKey)),
    setRowSelection: (rowSelection) => {
        set(() => ({ rowSelection: rowSelection }))
    },
    setRowRangeAnchor: (anchor) => set(() => ({ rowRangeAnchor: anchor })),
    setSelectionAnchor: (anchor) => set(() => ({ selectionAnchor: copyPos(anchor) })),
    loadData: () => set(() => ({ loadingData: true })),
    stopLoadingData: () => set(() => ({ loadingData: false })),
    refreshSummaries: () => scheduleSummariesRefresh(),
    updatePeriod: (period) => set(() => ({ period: period })),
    enableRowActions: (enable) => set(() => ({ rowActionsEnabled: enable })),
    updateMetaLines: (metaLines) => set(() => ({ metaLines: metaLines })),
    insertRecordIntoGroup: (parentRowId: string | null, newLine: Line): string => {
        const state = useDataStore.getState();
        const newMetaLines = new Map<string, MetaLine>(state.metaLines);
        let newRowId: string;
        let newData: Line[];

        if (parentRowId === null) {
            newRowId = String(state.data.length);
            newLine._rowId = newRowId;
            newData = [...state.data, newLine];
            newMetaLines.set(newRowId, { id: newRowId, line: newLine, parentId: null, childrenIds: [], level: 0 });
        } else {
            const parentMetaLine = state.metaLines.get(parentRowId);
            if (!parentMetaLine) return "";
            const isLeftGrouped = parentMetaLine.line.isLeftGrouped;
            const childrenNb = parentMetaLine.childrenIds.length;
            if (isLeftGrouped && childrenNb === 1) {
                const hasNoChildren = parentMetaLine.line.children![0].items.every((child) => child.readOnly === true);
                if (hasNoChildren) {
                    newRowId = parentRowId + ".0";
                } else {
                    newRowId = parentRowId + "." + childrenNb;
                }
            } else {
                newRowId = parentRowId + "." + childrenNb;
            }
            newLine._rowId = newRowId;
            const pathParts = parentRowId.split(".").map(Number);
            newData = insertOrReplaceLineAtPath(state.data, pathParts, newLine);
            newMetaLines.set(parentRowId, { ...parentMetaLine, childrenIds: [...parentMetaLine.childrenIds, newRowId] });
            newMetaLines.set(newRowId, { id: newRowId, line: newLine, parentId: parentRowId, childrenIds: [], level: parentMetaLine.level + 1 });
        }

        set((s) => ({
            data: newData,
            lineNumber: s.lineNumber + 1,
            metaLines: newMetaLines,
            focusPos: s.focusPos,
            editingPos: s.editingPos,
        }));
        return newRowId;
    },
}))